import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/index';
import { compact } from 'lodash';
import { Filter } from './filter-block/filter-item/filter';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AppConfigService } from '../../../config/app-config.service';
import { FilterPortalConfig } from '../../../config/filters/filter-portal-config.model';
import { FilterQueryConfig } from '../../../config/filters/filter-query-config.model';
import { FilterSearch } from '../filter-search';
import { FilterBlock } from './filter-block/filter-block';
import { TranslateService } from '../../../shared/translate.service';
import { AppUtils } from "../../../shared/app-utils";
import { HtmlUtilsService } from "../../../html-utils/html-utils.service";
import { MessageService } from '../../../messages/message.service';
import { QueryParams } from '../../../shared/list/query-params.model';
import { RichSearchList } from '../rich-search-list';
import {callNgModuleLifecycle} from '@angular/core/src/view/ng_module';
import {forEach} from '@angular/router/src/utils/collection';

@Injectable({
  providedIn: 'root'
})
export class FiltersService {

  allRegularFacets = new BehaviorSubject<Filter[]>([]);

  allRangeFacets = new BehaviorSubject<FilterSearch[]>([]);

  allPhraseFacets = new BehaviorSubject<string>('');

  moreAvailable: string[] = [];

  localFilters = new Map<string, FilterSearch>();

  rangeFilters = new Map<string, FilterSearch>();

  searchInText = false;
  tempFilters_q: Filter[] = [];

  constructor(protected router: Router,
    protected route: ActivatedRoute,
    protected appConfigService: AppConfigService,
    protected translator: TranslateService,
    protected htmlUtils: HtmlUtilsService,
    protected messageService: MessageService) {
  }

  canShow(filtersByField: Filter[], filterConfig: FilterPortalConfig, types: Filter[]) {
    return filtersByField && filterConfig.show && this.hasRightType(filterConfig, types);
  }

  navigateByFilters(remove: boolean = false, search?: boolean, removePhrase: boolean = false, valueFilter: string = "") {
    const queryParams = this.createQueryParams(remove, this.filtersToString(), removePhrase, valueFilter);
    const url = this.createUrl(search);
    if (this.calculateUrlLength(url, queryParams) < this.appConfigService.getConfig().maxUrlLength) {
      this.router.navigate([url], { queryParams: queryParams });
    } else {
      this.messageService.errorMessage('Filters.urlError.label', 'Filters.urlError.info');
    }

  }

  public setTempFilters_q(f: Filter, add: boolean) {
    if ((this.tempFilters_q.length == 0 && add))
      this.tempFilters_q.push(f);

    let tempFilter = this.tempFilters_q.filter(filter => filter.value == f.value)
    if (tempFilter.length == 0 && add) {
      //this.tempFilters_q = [];
      this.tempFilters_q.push(f);
    }
    else
      if (add == false) {
        this.tempFilters_q = [];
      }
  }
  public getTempFilters_q() {
    return this.tempFilters_q;
  }

  calculateUrlLengthWithFilters(filters: string) {
    return this.calculateUrlLength(this.createUrl(true), this.createQueryParams(false, filters), filters);
  }

  protected createQueryParams(remove: boolean = false, filters: string, removePhrase: boolean = false, valueFilter: string = ""): QueryParams {
    const queryParams = new QueryParams({
      phrase: removePhrase === false && filters.trim().length !== 0 ? this.allPhraseFacets.getValue() : "",
      page: 1,
      searchInText: this.htmlUtils.searchInText
    });
    let pageSize = this.route.snapshot.queryParamMap.get('pageSize');
    if (pageSize) {
      queryParams['pageSize'] = pageSize;
    }
    if (filters) {
      queryParams['filters'] = filters;
    }
    if (!remove) {
      queryParams['sort'] = this.route.snapshot.queryParamMap.get('sort');
    }
    let collectionId = this.route.snapshot.queryParamMap.get('collectionId');
    if (collectionId) {
      queryParams['collectionId'] = collectionId;
    }

    return queryParams;
  }

  private createUrl(search: boolean) {
    if (search !== undefined) {
      if (search) {
        return '/search/s'
      } else {
        return '/search/b'
      }
    } else {
      return HtmlUtilsService.getSearchUrl(AppUtils.getCurrentPage());
    }
  }

  private calculateUrlLength(url: string, queryParams: Params, filters?: string) {
    const urlLength = url.length;
    let urlConstParams = 'phrase'.length
      + '&'.length + 'page'.length + 1
      + '&'.length + 'searchInText'.length + 1;
    let urlExtraParams = 0;
    if (queryParams.phrase) {
      urlConstParams = queryParams.phrase.length;
    }
    if (queryParams['pageSize']) {
      urlExtraParams = '&'.length + 'pageSize'.length + (queryParams['pageSize'] + '').length;
    }
    if (queryParams['sort']) {
      urlExtraParams = '&'.length + 'sort'.length + queryParams['sort'].length;
    }
    if (filters) {
      urlExtraParams = '&'.length + 'filters'.length + encodeURIComponent(filters).length;
    } else if (queryParams['filters']) {
      urlExtraParams = '&'.length + 'filters'.length + encodeURIComponent(queryParams['filters']).length;
    }
    if (queryParams['collectionId']) {
      urlExtraParams = '&'.length + 'collectionId'.length + encodeURIComponent(queryParams['collectionId']).length;
    }

    return urlLength + urlConstParams + urlExtraParams;
  }

  selectedFilters(): Filter[] {
    return this.allRegularFacets.getValue().filter(filter => filter.selected);
  }

  cleanGroup(field: string) {
    this.selectedFilters()
      .filter(filterToCheck => filterToCheck.field == field)
      .forEach(filterToClean => {
        filterToClean.selected = false;
      });
    this.rangeFilters.delete(field);
    this.triggerRangeUpdate();
    this.triggerDependentFacetsUpdate(field);
    this.setTempFilters_q(new Filter(), false);
  }

  triggerDependentFacetsUpdate(field: string) {
    if (field && '_type' == field) {
      let presentFilters = this.selectedFilters();
      let types = this.selectedFilters().filter(filter => '_type' == filter.field);
      this.getConfigs().forEach(filterConfig => {
        this.clearIfCantShow(presentFilters, filterConfig, types);
        filterConfig.additionalConfigs.forEach(additionalConfig => {
          this.clearIfCantShow(presentFilters, additionalConfig, types);
        });
      });
    }
  }

  private clearIfCantShow(presentFilters, filterConfig, types) {
    let filtersByField = this.getFiltersByField(presentFilters, filterConfig.name);
    if (!this.canShow(filtersByField, filterConfig, types)) {
      this.selectedFilters()
        .filter(filterToCheck => filterToCheck.field == filterConfig.name)
        .forEach(filterToClean => {
          filterToClean.selected = false;
        });
      this.rangeFilters.delete(filterConfig.name);
    }
  }

  hideFilters() {
    return Array.from(this.localFilters.values())
      .concat(Array.from(this.rangeFilters.values()))
      .concat(this.selectedFilters()).filter(filter => filter.hideInResult).map(filter => {
        return new Filter(filter);
      });
  }

  filtersToString(skipType?: string): string {
    const allFilters = Array.from(this.localFilters.values())
      .concat(Array.from(this.rangeFilters.values()))
      .concat(this.selectedFilters());
    let filters = allFilters
      .filter(filter => skipType ? filter.field !== skipType : true)
      .map(filter => filter.toURL()).join(',');
    let childFilters = this.extractData(allFilters);
    childFilters ? childFilters = ',' + childFilters : null;
    return filters.concat(childFilters);
  }

  extractData(allFilters): string {
    return compact(allFilters.map(filtersy => {
      let children = null;
      if (filtersy.childFacetList) {
        children = this.extractData(filtersy.childFacetList);
      }
      if (children) {
        children = ',' + children;
        return this.returnChildFilters(filtersy).concat(children);
      } else {
        return this.returnChildFilters(filtersy);
      }
    })).join(',');
  }

  returnChildFilters(filter) {
    return filter.childFacetList ? filter.childFacetList.filter(child => child.selected === true).map(child => child.toURL()).join(',') : false;
  }

  toURL(filter): string {
    if (filter.range) {
      return `>${filter.field}:${this.getSafeValue(filter)}`;
    } else if (filter.skip) {
      return `-${filter.field}:${this.getSafeValue(filter)}`;
    } else if (filter.excluded) {
      return `${filter.field}:!${this.getSafeValue(filter)}`;
    } else if (filter.or) {
      return `${filter.field}:-${this.getSafeValue(filter)}`;
    } else {
      return `${filter.field}:${this.getSafeValue(filter)}`;
    }
  }

  getSafeValue(filter) {
    let txt = filter.value;
    if (txt) {
      txt = txt.replace('`', '');
    }

    if (/[-!;:,%'"?\/]/g.test(txt)) {
      return '`' + `${txt}` + '`';
    } else {
      return txt;
    }
  }


    translateByFieldName(field: string): string {
    let result = this.appConfigService.filtersConfig.filtersConfig
      .find(filterConfig => filterConfig.name == field || filterConfig.additionalConfigs.find(innerConfig => innerConfig.name == field) != null);
    if (result) {
      return this.translator.translate(result.translation);
    } else {
      return field;
    }
  }

  appendFilter(filter: Filter) {
    this.allRegularFacets.next([...this.allRegularFacets.value, filter]);
  }

  appendLocalFilter(filter: FilterSearch) {
    filter.skip = true;
    this.localFilters.set(filter.field, filter);
  }

  appendRangeFilter(filter: FilterSearch) {
    this.rangeFilters.set(filter.field, filter);
  }

  appendMoreAvailable(fieldNames: string[]) {
    this.moreAvailable = [];
    fieldNames.forEach(fieldName => {
      const filtersByName = this.allRegularFacets.value.filter(filter => filter.field == fieldName);
      if (filtersByName.find(filter => filter.selected)) {
        this.moreAvailable.push(fieldName);
      } else if (!filtersByName.find(filter => filter.count == 0)) {
        this.moreAvailable.push(fieldName);
      }
    });
  }

  static getAggregate(config: FilterPortalConfig[]) {
    const aggregates: FilterQueryConfig[] = [];
    config.forEach(filterConfig => {
      if (filterConfig.additionalConfigs.length > 0) {
        aggregates.push(...filterConfig.additionalConfigs);
        if (filterConfig.useAll) {
          aggregates.push(filterConfig);
        }
      } else {
        aggregates.push(filterConfig);
      }
    });
    return aggregates;
  }

  getAggregates(): FilterQueryConfig[] {
    return FiltersService.getAggregate(
      this.appConfigService.filtersConfig.filtersConfig.filter(filter => filter.show));
  }

  getConfigs(): FilterPortalConfig[] {
    return this.appConfigService.filtersConfig.filtersConfig;
  }

  consume(filters: FilterSearch[], appendFunction: (filters: FilterSearch) => void, beforeFunction?: Function, afterFunction?: Function) {
    if (beforeFunction) {
      beforeFunction.apply(this);
    }
    if (filters) {
      filters.forEach(filter => appendFunction.call(this, filter));
    }
    if (afterFunction) {
      afterFunction.apply(this);
    }
  }

  clearRanges() {
    this.rangeFilters.clear();
  }

  triggerRangeUpdate() {
    this.allRangeFacets.next(Array.from(this.rangeFilters.values()));
  }

  hasResults(filterConfig: FilterPortalConfig, childFiltersByField: FilterBlock[], filtersByField: Filter[]) {
    return (filterConfig.force || childFiltersByField.length > 0 || filtersByField.length > 0);
  }

  getFiltersByField(filters, name: string): Filter[] {
    return filters.filter(facet => name == facet.field && !facet.hideInResult);
  }

  getChildFilterBlocks(additionalConfigs: FilterPortalConfig[], filters: Filter[]): FilterBlock[] {

    const childFiltersByField: FilterBlock[] = [];
    additionalConfigs.forEach(additionalConfig => {
      const childFilters = this.getFiltersByField(filters, additionalConfig.name);
      if (childFilters && childFilters.length > 0) {
        childFiltersByField.push(new FilterBlock(additionalConfig, childFilters));
      }
    });
    return childFiltersByField;
  }

  markAsSearchInText(value: boolean) {
    this.searchInText = value;
  }

  consumeFacets(searchList: RichSearchList) {
    this.allRegularFacets.next(searchList.facets.filter(facet => facet.value));
    this.consume(searchList.filters.filter(filter => filter.range), this.appendRangeFilter, this.clearRanges, this.triggerRangeUpdate);
    this.consume(searchList.filters.filter(filter => filter.skip), this.appendLocalFilter);
    this.appendMoreAvailable(searchList.moreAvailable);
  }

  private hasRightType(filterConfig: FilterPortalConfig, types: Filter[]) {
    const configTypes = filterConfig.types.sort();
    let currentTypes = types.map(filter => filter.value).sort();
    if (currentTypes.length == 0) {
      currentTypes = FilterQueryConfig.ALL;
    }
    return configTypes.length == 0 || this.includesAllRight(configTypes, currentTypes);
  }

  private includesAllRight(configTypes: string[], currentTypes: string[]) {
    return currentTypes.filter(filter => !configTypes.includes(filter)).length == 0;
  }
}
